import { Injectable } from '@angular/core';
import { DateUtilsService } from '../helpers/date-utils.service';
import { INDICADORES_PAGOS_CONSTANTS } from '../../finanzas/components/pagos/indicadores-pagos.constants';

/**
 * Business logic for the Pagos indicators panel (TOM-388).
 *
 * Category: Business Logic Service
 * Purpose: business-hours validation time (in "cortes" elapsed, not raw minutes),
 * validados/rechazados/facturas totals, and the per-forma-de-pago breakdown.
 *
 * Kept framework/Firebase-free on purpose (only Angular's own DI decorator is used) so this
 * can be ported to trosten-ventas-next largely as-is when that follow-up ticket happens.
 */
@Injectable()
export class IndicadoresPagosService {

    private feriadosPorAnio: { [anio: number]: number[] } = {};

    constructor(private dateUtilsService: DateUtilsService) { }

    /**
     * The n-th occurrence of a weekday in a given month (weekday: 0=Sunday..6=Saturday).
     * Used for the three "n-ésimo lunes" Mexican holidays (Constitución, Juárez, Revolución).
     */
    nthWeekdayOfMonth(year: number, monthIndex0: number, weekday: number, n: number): Date {
        const first = new Date(year, monthIndex0, 1);
        const offset = (7 + weekday - first.getDay()) % 7;
        return new Date(year, monthIndex0, 1 + offset + (n - 1) * 7);
    }

    /**
     * The 7 días de descanso obligatorio (Ley Federal del Trabajo) for a given year, as
     * midnight timestamps. Cached per year. Explicitly does NOT include 2-nov or Semana Santa —
     * Finanzas confirmed those count as normal business days for this indicator.
     */
    feriadosOficialesDelAnio(anio: number): number[] {
        if (!this.feriadosPorAnio[anio]) {
            const fechas = [
                new Date(anio, 0, 1),                                  // Año Nuevo
                this.nthWeekdayOfMonth(anio, 1, 1, 1),                 // Constitución: 1er lunes de febrero
                this.nthWeekdayOfMonth(anio, 2, 1, 3),                 // Natalicio de Juárez: 3er lunes de marzo
                new Date(anio, 4, 1),                                  // Día del Trabajo
                new Date(anio, 8, 16),                                 // Independencia
                this.nthWeekdayOfMonth(anio, 10, 1, 3),                // Revolución: 3er lunes de noviembre
                new Date(anio, 11, 25)                                 // Navidad
            ];
            this.feriadosPorAnio[anio] = fechas.map(f => new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime());
        }
        return this.feriadosPorAnio[anio];
    }

    /** Sábado/domingo, o uno de los 7 feriados oficiales. */
    esDiaInhabilMx(date: Date): boolean {
        const dia = date.getDay();
        if (dia === 0 || dia === 6) {
            return true;
        }
        const medianoche = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        return this.feriadosOficialesDelAnio(date.getFullYear()).indexOf(medianoche) > -1;
    }

    /** Next corte cutoff strictly after `cutoff` (which must itself be exactly at a cutoff hour). */
    private siguienteCutoff(cutoff: Date): Date {
        const { CORTE_1, CORTE_2 } = INDICADORES_PAGOS_CONSTANTS.CORTE_HOURS;
        if (cutoff.getHours() === CORTE_1) {
            return new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate(), CORTE_2, 0, 0, 0);
        }
        let next = new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate() + 1, CORTE_1, 0, 0, 0);
        while (this.esDiaInhabilMx(next)) {
            next = new Date(next.getFullYear(), next.getMonth(), next.getDate() + 1, CORTE_1, 0, 0, 0);
        }
        return next;
    }

    /** Corte cutoff strictly before `cutoff` (which must itself be exactly at a cutoff hour). */
    private cutoffAnterior(cutoff: Date): Date {
        const { CORTE_1, CORTE_2 } = INDICADORES_PAGOS_CONSTANTS.CORTE_HOURS;
        if (cutoff.getHours() === CORTE_2) {
            return new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate(), CORTE_1, 0, 0, 0);
        }
        let prev = new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate() - 1, CORTE_2, 0, 0, 0);
        while (this.esDiaInhabilMx(prev)) {
            prev = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1, CORTE_2, 0, 0, 0);
        }
        return prev;
    }

    /** The corte that will process an event happening at `ms` — first cutoff >= ms. */
    corteAsignado(ms: number): Date {
        const { CORTE_1, CORTE_2 } = INDICADORES_PAGOS_CONSTANTS.CORTE_HOURS;
        const d = new Date(ms);
        if (this.esDiaInhabilMx(d)) {
            let next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, CORTE_1, 0, 0, 0);
            while (this.esDiaInhabilMx(next)) {
                next = new Date(next.getFullYear(), next.getMonth(), next.getDate() + 1, CORTE_1, 0, 0, 0);
            }
            return next;
        }
        const corte1 = new Date(d.getFullYear(), d.getMonth(), d.getDate(), CORTE_1, 0, 0, 0);
        if (ms <= corte1.getTime()) {
            return corte1;
        }
        const corte2 = new Date(d.getFullYear(), d.getMonth(), d.getDate(), CORTE_2, 0, 0, 0);
        if (ms <= corte2.getTime()) {
            return corte2;
        }
        return this.siguienteCutoff(corte2);
    }

    /** The most recent corte that has already run as of `ms` — last cutoff <= ms. */
    corteEjecutadoMasReciente(ms: number): Date {
        const { CORTE_1, CORTE_2 } = INDICADORES_PAGOS_CONSTANTS.CORTE_HOURS;
        const d = new Date(ms);
        if (this.esDiaInhabilMx(d)) {
            let prev = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1, CORTE_2, 0, 0, 0);
            while (this.esDiaInhabilMx(prev)) {
                prev = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1, CORTE_2, 0, 0, 0);
            }
            return prev;
        }
        const corte2 = new Date(d.getFullYear(), d.getMonth(), d.getDate(), CORTE_2, 0, 0, 0);
        if (ms >= corte2.getTime()) {
            return corte2;
        }
        const corte1 = new Date(d.getFullYear(), d.getMonth(), d.getDate(), CORTE_1, 0, 0, 0);
        if (ms >= corte1.getTime()) {
            return corte1;
        }
        return this.cutoffAnterior(corte1);
    }

    /**
     * Cortes elapsed between the corte an upload was assigned to and the corte batch that was
     * running when it got validated/rechazado. Negative = validated ahead of its own corte.
     */
    cortesTranscurridos(inicioMs: number, validacionMs: number): number {
        const asignado = this.corteAsignado(inicioMs);
        const ejecutado = this.corteEjecutadoMasReciente(validacionMs);
        if (ejecutado.getTime() === asignado.getTime()) {
            return 0;
        }
        let cursor = asignado;
        let count = 0;
        if (ejecutado.getTime() > asignado.getTime()) {
            while (cursor.getTime() < ejecutado.getTime()) {
                cursor = this.siguienteCutoff(cursor);
                count++;
            }
            return count;
        }
        while (cursor.getTime() > ejecutado.getTime()) {
            cursor = this.cutoffAnterior(cursor);
            count++;
        }
        return -count;
    }

    clasificarCorte(diff: number): string {
        if (diff < 0) {
            return 'anticipado';
        }
        if (diff === 0) {
            return 'enSuCorte';
        }
        if (diff === 1) {
            return 'mas1';
        }
        if (diff === 2) {
            return 'mas2';
        }
        return 'mas3';
    }

    /**
     * Clock start for the validation-time metric. Orden de compra doesn't start the clock at
     * comprobante upload — it starts 1 día after the LATEST finDeServicio among the confirmación's
     * servicios (a confirmación can have several; the old screen only looked at servicios[0]).
     */
    clockStart(pago: any, servicios: any[]): number {
        if (pago.formaDePago === 'Orden de compra') {
            const finDeServicioMax = (servicios || []).reduce((max, s) => {
                const fin = (s && (s.finDeServicio || s.inicioDeServicio)) || 0;
                return fin > max ? fin : max;
            }, 0);
            if (finDeServicioMax) {
                return finDeServicioMax + 86400000;
            }
        }
        return pago.fechaSubidaComprobante;
    }

    /**
     * Same predicate as FiltrarPagosPipe, extracted here so the pipe delegates to this method —
     * table and indicators panel can never disagree on what "filtered" means.
     */
    filtroPago(pago: any, formaPago: any, estatus: any, terminoBusqueda: any): boolean {
        if (formaPago && pago.formaDePago != formaPago) {
            return false;
        }
        if (estatus) {
            if (estatus === 'Confirmado') {
                if (!(pago.estatus != 'Cancelado' && pago.aprobacionFinanzas == true)) {
                    return false;
                }
            } else if (!(pago.estatus == estatus && pago.aprobacionFinanzas == false)) {
                return false;
            }
        }
        if (terminoBusqueda) {
            const idServicio = String(pago.confirmacionIdTrosten || '');
            if (idServicio.toLowerCase().indexOf(String(terminoBusqueda).toLowerCase()) === -1) {
                return false;
            }
        }
        return true;
    }

    /** Main aggregator — expects an already date-range-loaded, already filtroPago-filtered list. */
    calcularIndicadoresPagos(pagosFiltrados: any[]): any {
        const buckets = { anticipado: 0, enSuCorte: 0, mas1: 0, mas2: 0, mas3: 0, count: 0, sumMinutes: 0 };
        let validados = 0;
        let rechazados = 0;
        let pendientes = 0;
        let facturas = 0;
        const porFormaDePago: any = {};
        const { START_HOUR, END_HOUR } = INDICADORES_PAGOS_CONSTANTS.BUSINESS_HOURS;

        (pagosFiltrados || []).forEach(pago => {
            const esValidado = pago.estatus !== 'Rechazado' && pago.aprobacionFinanzas === true;
            const esRechazado = pago.estatus === 'Rechazado';
            const requiereFactura = !!(pago.infoConfirmacion && pago.infoConfirmacion.requiereFactura);

            if (esValidado) {
                validados++;
            }
            if (esRechazado) {
                rechazados++;
            }
            // Ni validado ni rechazado: Finanzas todavía no lo resolvió. Definido por descarte a
            // propósito, para que validados + rechazados + pendientes dé siempre el TOTAL — que es
            // justo la cuenta que no cerraba a la vista y hacía que Finanzas preguntara de dónde
            // salía el denominador de los buckets (que sólo cuenta resueltos).
            if (!esValidado && !esRechazado) {
                pendientes++;
            }
            if (requiereFactura) {
                facturas++;
            }

            const forma = pago.formaDePago;
            if (forma) {
                if (!porFormaDePago[forma]) {
                    porFormaDePago[forma] = { validados: 0, rechazados: 0, facturas: 0 };
                }
                if (esValidado) {
                    porFormaDePago[forma].validados++;
                }
                if (esRechazado) {
                    porFormaDePago[forma].rechazados++;
                }
                if (requiereFactura) {
                    porFormaDePago[forma].facturas++;
                }
            }

            const fechaResolucion = esRechazado ? pago.fechaRechazoFinanzas : (esValidado ? pago.fechaValidacionFinanzas : null);
            if (fechaResolucion) {
                const inicio = this.clockStart(pago, pago.serviciosDeConfirmacion);
                if (inicio) {
                    const diff = this.cortesTranscurridos(inicio, fechaResolucion);
                    const bucket = this.clasificarCorte(diff);
                    buckets[bucket]++;
                    buckets.count++;
                    // getBusinessElapsedMinutes takes OFF-hours boundaries (start, end), not business
                    // hours — business 08:00-18:00 means off-hours starts at 18 and ends at 8.
                    buckets.sumMinutes += this.dateUtilsService.getBusinessElapsedMinutes(
                        inicio, fechaResolucion, END_HOUR, START_HOUR, d => this.esDiaInhabilMx(d)
                    );
                }
            }
        });

        const promedioMinutos = buckets.count > 0 ? Math.round(buckets.sumMinutes / buckets.count) : 0;
        const total = pagosFiltrados ? pagosFiltrados.length : 0;

        return {
            buckets,
            promedio: this.dateUtilsService.formatMinutes(promedioMinutos),
            validados,
            rechazados,
            pendientes,
            // Sobre el TOTAL de la semana, no sobre buckets.count: es el denominador que ya usa la
            // grilla por forma de pago y el único visible en esa línea, así que la cuenta se puede
            // verificar a ojo. Con pendientes en la línea los tres suman 100% (salvo centésimas de
            // redondeo) y validados + rechazados + pendientes da el TOTAL.
            porcentajeValidados: this.porcentajeSobreTotal(validados, total),
            porcentajeRechazados: this.porcentajeSobreTotal(rechazados, total),
            porcentajePendientes: this.porcentajeSobreTotal(pendientes, total),
            total,
            facturas,
            porFormaDePago
        };
    }

    /**
     * SLA card for the Concentrado de Cotizaciones (TOM-395). Two independent legs over the
     * same pago population as calcularIndicadoresPagos: how long Ventas took to subir the
     * comprobante, and how long Finanzas took to validate it (byte-identical math to
     * calcularIndicadoresPagos, reusing clockStart/cortesTranscurridos/clasificarCorte/
     * esDiaInhabilMx verbatim so both screens can never disagree).
     */
    calcularSlaPagos(pagosConConfirmacion: any[]): any {
        const { START_HOUR, END_HOUR } = INDICADORES_PAGOS_CONSTANTS.BUSINESS_HOURS;
        let ventasCount = 0;
        let ventasSumMinutes = 0;
        const buckets = { anticipado: 0, enSuCorte: 0, mas1: 0, mas2: 0, mas3: 0, count: 0, sumMinutes: 0 };

        (pagosConConfirmacion || []).forEach(pago => {
            const fechaConfirmacion = pago.infoConfirmacion && pago.infoConfirmacion.fechaDeConfirmacion;
            if (fechaConfirmacion && pago.fechaSubidaComprobante) {
                ventasCount++;
                ventasSumMinutes += this.dateUtilsService.getBusinessElapsedMinutes(
                    fechaConfirmacion, pago.fechaSubidaComprobante, END_HOUR, START_HOUR, d => this.esDiaInhabilMx(d)
                );
            }

            const esRechazado = pago.estatus === 'Rechazado';
            const esValidado = !esRechazado && pago.aprobacionFinanzas === true;
            const fechaResolucion = esRechazado ? pago.fechaRechazoFinanzas : (esValidado ? pago.fechaValidacionFinanzas : null);
            if (fechaResolucion) {
                const inicio = this.clockStart(pago, pago.serviciosDeConfirmacion);
                if (inicio) {
                    const diff = this.cortesTranscurridos(inicio, fechaResolucion);
                    const bucket = this.clasificarCorte(diff);
                    buckets[bucket]++;
                    buckets.count++;
                    buckets.sumMinutes += this.dateUtilsService.getBusinessElapsedMinutes(
                        inicio, fechaResolucion, END_HOUR, START_HOUR, d => this.esDiaInhabilMx(d)
                    );
                }
            }
        });

        const ventasPromedioMinutos = ventasCount > 0 ? Math.round(ventasSumMinutes / ventasCount) : 0;
        const finanzasPromedioMinutos = buckets.count > 0 ? Math.round(buckets.sumMinutes / buckets.count) : 0;

        return {
            ventas: {
                promedio: this.dateUtilsService.formatMinutes(ventasPromedioMinutos),
                count: ventasCount
            },
            finanzas: {
                promedio: this.dateUtilsService.formatMinutes(finanzasPromedioMinutos),
                buckets,
                count: buckets.count
            }
        };
    }

    private porcentajeSobreTotal(cantidad: number, total: number): string {
        if (!total) {
            return '0.0';
        }
        return (cantidad * 100 / total).toFixed(1);
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/indicadores-pagos.service.ts